import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import defaultAriaProps from 'client/utils/constants/ariaPropTypes';
import styles from './Tooltip.module.scss';

const Tooltip = ({ children, isOpen, onClose, triggerRef, contentClassName, ...props }) => {
    const tooltipRef = useRef(null);

    useEffect(() => {
        if (!isOpen || !onClose) {
            return;
        }

        const handleClickOutside = (event) => {
            if (
                tooltipRef.current &&
                !tooltipRef.current.contains(event.target) &&
                triggerRef?.current &&
                !triggerRef.current.contains(event.target)
            ) {
                onClose();
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose, triggerRef]);

    if (!isOpen) {
        return null;
    }

    const testId = props['data-testid'] || null;

    return (
        <div
            ref={tooltipRef}
            className={[styles.tooltip, props.className].filter(Boolean).join(' ')}
            role="status"
            data-testid={testId}
            {...props}
        >
            <div className={[styles.content, contentClassName].filter(Boolean).join(' ')} data-testid={testId ? `${testId}.content` : null}>
                {children}
            </div>
            <div className={styles.arrow} data-testid={testId ? `${testId}.arrow` : null} />
        </div>
    );
};

Tooltip.propTypes = {
    ...defaultAriaProps,
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    triggerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    contentClassName: PropTypes.string
};

export default Tooltip;
